import { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { authService } from '../../../lib/api/apiService';

const createResetPasswordSchema = () =>
    z.object({
        password: z
            .string()
            .min(8, 'Le mot de passe doit contenir au moins 8 caractères')
            .regex(/[A-Z]/, 'Le mot de passe doit contenir au moins une majuscule')
            .regex(/[a-z]/, 'Le mot de passe doit contenir au moins une minuscule')
            .regex(/\d/, 'Le mot de passe doit contenir au moins un chiffre')
            .regex(/[!@#$%^&*(),.?":{}|<>]/, 'Le mot de passe doit contenir au moins un caractère spécial'),
        confirmPassword: z.string().min(1, 'Veuillez confirmer le mot de passe'),
    }).refine((data) => data.password === data.confirmPassword, {
        message: 'Les mots de passe ne correspondent pas',
        path: ['confirmPassword'],
    });

const ResetPassword = () => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [tokenValid, setTokenValid] = useState(true);

    const { token } = useParams();
    const navigate = useNavigate();
    const schema = useMemo(() => createResetPasswordSchema(), []);

    // Vérifier la validité du token au chargement
    useEffect(() => {
        const verifyToken = async () => {
            try {
                const result = await authService.verifyResetToken(token);
                console.log('result', result);
                if (!result.success) {
                    setTokenValid(false);
                    setError('Le lien de réinitialisation est invalide ou a expiré');
                }
            } catch (error) {
                setTokenValid(false);
                setError('Le lien de réinitialisation est invalide ou a expiré');
            }
        };

        if (token) {
            verifyToken();
        } else {
            setTokenValid(false);
            setError('Token manquant');
        }
    }, [token]);

    const validatePassword = (password) => {
        const minLength = 8;
        const hasUpperCase = /[A-Z]/.test(password);
        const hasLowerCase = /[a-z]/.test(password);
        const hasNumbers = /\d/.test(password);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

        return {
            isValid: password.length >= minLength && hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChar,
            errors: {
                minLength: password.length < minLength,
                hasUpperCase: !hasUpperCase,
                hasLowerCase: !hasLowerCase,
                hasNumbers: !hasNumbers,
                hasSpecialChar: !hasSpecialChar
            }
        };
    };

    const passwordValidation = validatePassword(password);
    const passwordsMatch = password === confirmPassword;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const result = schema.safeParse({ password, confirmPassword });
        if (!result.success) {
            setError(result.error.issues[0]?.message || 'Le mot de passe ne respecte pas les critères requis');
            return;
        }

        setLoading(true);

        try {
            const result = await authService.resetPassword(token, password, confirmPassword);
            
            if (result.success) {
                setSuccess(true);
                // Rediriger vers la page de connexion après 3 secondes
                setTimeout(() => {
                    navigate('/vendor/login');
                }, 3000);
            } else {
                setError(result.message || 'Erreur lors de la réinitialisation du mot de passe');
            }
        } catch (error) {
            console.error('Erreur lors de la réinitialisation:', error);
            setError('Une erreur inattendue s\'est produite');
        } finally {
            setLoading(false);
        }
    };

    if (!tokenValid) {
        return (
            <div className="min-h-screen flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                    style={{
                        backgroundImage: 'url("/images/auth/background-image.png")'
                    }}>
                </div>

                <div className="relative z-10 flex w-full max-w-6xl justify-between">
                    <div className="w-full lg:w-1/2 flex items-center justify-center">
                        <div className="bg-white rounded-3xl shadow-2xl p-10 w-full max-w-md text-center">
                            <div className="flex justify-center mb-6">
                                <img src="/images/logo.png" alt="logo" className="w-auto h-14" />
                            </div>

                            <div className="text-red-600 mb-6">
                                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                            </div>

                            <h2 className="text-xl font-bold text-gray-800 mb-4">
                                Lien invalide
                            </h2>

                            <p className="text-gray-600 text-sm mb-6">
                                {error || 'Ce lien de réinitialisation est invalide ou a expiré. Veuillez demander un nouveau lien.'}
                            </p>

                            <Link
                                to="/forgot-password"
                                className="inline-block w-full bg-orange-hover hover:bg-orange-600 text-white text-base py-2 px-6 rounded-full transform transition-all duration-200 shadow-lg hover:scale-105"
                            >
                                Demander un nouveau lien
                            </Link>

                            <div className="mt-4">
                                <Link
                                    to="/vendor/login"
                                    className="text-sm text-gray-600 hover:text-orange-light font-medium transition-colors"
                                >
                                    ← Retour à la connexion
                                </Link>
                            </div>
                        </div>
                    </div>

                    <div className="hidden lg:flex lg:w-1/3 items-end justify-end">
                        <div className="text-white max-w-md text-center">
                            <h2 className="text-xl font-medium text-right mb-4">
                                Sécurité renforcée
                            </h2>
                            <p className="text-sm text-gray-200 text-right leading-relaxed">
                                Nos liens de réinitialisation ont une durée de vie limitée pour garantir 
                                la sécurité de votre compte.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center p-4">
            {/* Background Image with Overlay */}
            <div className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                style={{
                    backgroundImage: 'url("/images/auth/background-image.png")'
                }}>
            </div>

            <div className="relative z-10 flex w-full max-w-6xl justify-between">
                {/* Reset Password Form - Left Side */}
                <div className="w-full lg:w-1/2 flex items-center justify-center">
                    <div className="bg-white rounded-3xl shadow-2xl p-10 w-full max-w-md">
                        <div className="flex justify-center">
                            <img src="/images/logo.png" alt="logo" className="w-auto h-14" />
                        </div>

                        {/* Welcome Message */}
                        <div className="text-center mb-8 mt-4">
                            <h2 className="text-xl font-bold text-gray-800 mb-2">
                                Nouveau mot de passe
                            </h2>
                            <p className="text-gray-600 text-sm">
                                Entrez votre nouveau mot de passe ci-dessous
                            </p>
                        </div>

                        {/* Success Message */}
                        {success && (
                            <div className="mb-4 p-4 bg-green-100 border border-green-300 text-green-700 rounded-lg text-sm">
                                <div className="flex items-center">
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Mot de passe réinitialisé avec succès ! Redirection en cours...
                                </div>
                            </div>
                        )}

                        {/* Error Message */}
                        {error && (
                            <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
                                {error}
                            </div>
                        )}

                        {/* Reset Password Form */}
                        {!success ? (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                {/* Password Field */}
                                <div>
                                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                                        Nouveau mot de passe
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            id="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="Entrez votre nouveau mot de passe"
                                            className={`text-sm w-full px-4 py-3 pr-12 border rounded-full focus:outline-none focus:ring-2 focus:ring-orange-light focus:border-transparent transition-all duration-200 ${
                                                password && !passwordValidation.isValid ? 'border-red-300' : 'border-gray-300'
                                            }`}
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                        >
                                            {showPassword ? (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49"/>
                                                    <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242"/>
                                                    <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143"/>
                                                    <path d="m2 2 20 20"/>
                                                </svg>
                                            ) : (
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                    
                                    {/* Password Requirements */}
                                    {password && (
                                        <div className="mt-2 text-xs">
                                            <div className={`flex items-center ${passwordValidation.errors.minLength ? 'text-red-600' : 'text-green-600'}`}>
                                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                                Au moins 8 caractères
                                            </div>
                                            <div className={`flex items-center ${passwordValidation.errors.hasUpperCase ? 'text-red-600' : 'text-green-600'}`}>
                                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                                Une majuscule
                                            </div>
                                            <div className={`flex items-center ${passwordValidation.errors.hasLowerCase ? 'text-red-600' : 'text-green-600'}`}>
                                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                                Une minuscule
                                            </div>
                                            <div className={`flex items-center ${passwordValidation.errors.hasNumbers ? 'text-red-600' : 'text-green-600'}`}>
                                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                                Un chiffre
                                            </div>
                                            <div className={`flex items-center ${passwordValidation.errors.hasSpecialChar ? 'text-red-600' : 'text-green-600'}`}>
                                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                                Un caractère spécial
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Confirm Password Field */}
                                <div>
                                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                                        Confirmer le mot de passe
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            id="confirmPassword"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            placeholder="Confirmez votre nouveau mot de passe"
                                            className={`text-sm w-full px-4 py-3 pr-12 border rounded-full focus:outline-none focus:ring-2 focus:ring-orange-light focus:border-transparent transition-all duration-200 ${
                                                confirmPassword && !passwordsMatch ? 'border-red-300' : 'border-gray-300'
                                            }`}
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                        >
                                            {showConfirmPassword ? (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49"/>
                                                    <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242"/>
                                                    <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143"/>
                                                    <path d="m2 2 20 20"/>
                                                </svg>
                                            ) : (
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                    
                                    {/* Password Match Indicator */}
                                    {confirmPassword && (
                                        <div className="mt-1 text-xs">
                                            <div className={`flex items-center ${passwordsMatch ? 'text-green-600' : 'text-red-600'}`}>
                                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                                {passwordsMatch ? 'Les mots de passe correspondent' : 'Les mots de passe ne correspondent pas'}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={loading || !passwordValidation.isValid || !passwordsMatch}
                                    className={`w-full text-white text-base py-2 px-6 rounded-full transform transition-all duration-200 shadow-lg ${
                                        loading || !passwordValidation.isValid || !passwordsMatch
                                            ? 'bg-gray-400 cursor-not-allowed' 
                                            : 'bg-orange-hover hover:bg-orange-600 hover:scale-105'
                                    }`}
                                >
                                    {loading ? (
                                        <div className="flex items-center justify-center">
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Réinitialisation en cours...
                                        </div>
                                    ) : (
                                        'Réinitialiser le mot de passe'
                                    )}
                                </button>
                            </form>
                        ) : (
                            /* Success State */
                            <div className="text-center space-y-4">
                                <div className="text-green-600">
                                    <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <p className="text-gray-600 text-sm">
                                    Votre mot de passe a été réinitialisé avec succès ! 
                                    Vous allez être redirigé vers la page de connexion.
                                </p>
                            </div>
                        )}

                        {/* Back to Login */}
                        <div className="text-center mt-6">
                            <Link
                                to="/vendor/login"
                                className="text-sm text-gray-600 hover:text-orange-light font-medium transition-colors"
                            >
                                ← Retour à la connexion
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Marketing Message - Right Side */}
                <div className="hidden lg:flex lg:w-1/3 items-end justify-end">
                    <div className="text-white max-w-md text-center">
                        <h2 className="text-xl font-medium text-right mb-4">
                            Mot de passe sécurisé
                        </h2>
                        <p className="text-sm text-gray-200 text-right leading-relaxed">
                            Choisissez un mot de passe fort pour protéger votre compte. 
                            Un mot de passe sécurisé est votre première ligne de défense.
                        </p>
                        
                        <div className="flex justify-end space-x-1 mt-6">
                            <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                            <div className="w-2 h-2 rounded-full bg-white"></div>
                            <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
